import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import { Subscription, tap } from 'rxjs';

import { FieldConfig } from '@shared/restaurant/models';
import { FieldType } from '@shared/restaurant/enums';
import {
  emailRegexp,
  urlValidator,
  ValidationService,
  CustomDateParserFormatterProvider,
} from '@tablesready/common-ui';
import { CustomDateAdapterProvider } from './providers';
import { AppIcon } from '@tablesready/frontend-icons';

interface FormValidators {
  validators: Validators[];
  asyncValidators: AsyncValidator[] | AsyncValidatorFn[];
  updateOn?: string;
}

@Component({
  selector: 'tablesready-party-custom-fields',
  templateUrl: './party-custom-fields.component.html',
  styleUrls: ['./party-custom-fields.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PartyCustomFieldsComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: PartyCustomFieldsComponent,
      multi: true,
    },
    CustomDateAdapterProvider,
    CustomDateParserFormatterProvider,
  ],
})
export class PartyCustomFieldsComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy, Validator {
  @Input() config: FieldConfig[];
  @Input() manager = true;
  @Input() country: string = 'US';
  @Input() publicPages = false;
  group: FormGroup;
  fieldTypes = FieldType;
  value: any = {};
  icon = AppIcon;

  private onChangeMethod: (value: number | string | boolean) => unknown;
  private onTouchedMethod: () => unknown;
  private readonly subscriptions = new Subscription();
  private validatorsByType: Record<string, FormValidators>;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly validationService: ValidationService,
  ) {
    this.validatorsByType = {
      [FieldType.text]: { validators: [], asyncValidators: [] },
      [FieldType.number]: { validators: [], asyncValidators: [] },
      [FieldType.email]: {
        validators: [Validators.pattern(emailRegexp)],
        asyncValidators: [],
      },
      [FieldType.url]: { validators: [urlValidator], asyncValidators: [] },
      [FieldType.tel]: {
        validators: [this.validationService.phonePatternCountryValidator(() => this.country)],
        asyncValidators: [],
      },
      [FieldType.date]: { validators: [], asyncValidators: [] },
      [FieldType.select]: { validators: [], asyncValidators: [] },
      [FieldType.multiSelect]: { validators: [], asyncValidators: [] },
    };
  }

  ngOnInit(): void {
    this.initControls();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.initControls();
      this.group?.patchValue(this.value);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  registerOnChange(onChange: (_: any) => void): void {
    this.onChangeMethod = onChange;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouchedMethod = fn;
  }

  writeValue(value: any) {
    if (value === null) {
      this.group?.reset();

      return;
    }

    this.value = value || {};
    this.group?.patchValue(value || {});
  }

  fieldAvailable(field: FieldConfig): boolean {
    if (this.manager) {
      return field.enabled && field.visibility.private.add;
    }

    return field.enabled && field.visibility.public.add;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!this.group) {
      return;
    }

    isDisabled ? this.group.disable() : this.group.enable();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.group?.invalid && { invalid: true } || null;
  }

  getFields() {
    return this.config?.filter(field => {
      return field.enabled && (this.manager || field.visibility.public.add)
    });
  }

  private initControls(): void {
    const definition: {[key: string]: [null, FormValidators]} = {};
    const fields = this.getFields();

    if (fields && fields.length > 0) {
      for (const field of fields) {
        const rules = this.validatorsByType[field.type];
        // Create a copy of validators to avoid mutating the shared array
        const fieldValidators = [...rules.validators];

        if (!this.manager && field.visibility.public.required) {
          fieldValidators.unshift(Validators.required);
        }

        if (this.manager && field.visibility.private.required) {
          fieldValidators.unshift(Validators.required);
        }

        definition[field.id] = [null, {
          validators: fieldValidators,
          asyncValidators: rules.asyncValidators
        }];
      }

      this.group = this.formBuilder.group(definition);
      const valueChangesSubscription = this.group.valueChanges.pipe(
        tap(value => {
          if (this.onChangeMethod) {
            this.onChangeMethod(value);
          }
        }),
      )
        .subscribe();
      this.subscriptions.add(valueChangesSubscription);
    }
  }
}
